const isObject = (v: any): boolean => {
  return v && typeof v === 'object' && !Array.isArray(v);
};

const coerceValue = (val: any, type: any): any => {
  switch (type) {
    case 'string':
      return String(val || (val === 0 ? 0 : ''));
    case 'number':
      return val ? parseInt(val, 10) : val;
    case 'float':
      return val ? parseFloat(val) : val;
    case 'date':
      return val ? new Date(val) : val;
    case 'boolean':
      return !!val;
    default:
      return val;
  }
};

export class JsonApiSerializer {
  schema: Record<any, any> = {};

  constructor({ schema }: { schema?: Record<any, any> } = {}) {
    this.schema = schema || {};
  }

  serialize = (
    type: string,
    attrs: Record<any, any> | Array<Record<any, any>>
  ): Record<any, any> | null => {
    if (!attrs) {
      return null;
    }

    if (Array.isArray(attrs)) {
      return attrs.map((rec) => this.parseResource(type, rec));
    }

    return this.parseResource(type, attrs);
  };

  parseResource = (
    type: string,
    attrs: Record<any, any> = {}
  ): {
    type: string;
    id?: string;
    attributes?: Record<any, any>;
    relationships?: Record<any, any>;
  } | null => {
    if (!attrs) {
      return null;
    }

    attrs = { ...attrs };

    if (attrs._type) {
      type = attrs._type;
      delete attrs._type;
    }

    const data: {
      type: string;
      id?: string;
      attributes?: Record<any, any>;
      relationships?: Record<any, any>;
    } = { type };
    const rels: Record<string, any> = {};

    if (attrs.id) {
      data.id = String(attrs.id);
      delete attrs.id;
    }

    const config = this.schema[type];
    if (!config) {
      return { ...data, attributes: attrs };
    }

    for (const field in config.relationships) {
      if (attrs[field] === undefined) {
        continue;
      }

      const ref = config.relationships[field];
      const val = attrs[field];

      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete attrs[field];

      const relType = ref.type || (ref.getType ? ref.getType(attrs) : null);

      if (!ref.readOnly) {
        if (Array.isArray(val)) {
          rels[field] = {
            data: val.map((v) => this.parseRelationship(relType, v)),
          };
        } else {
          if (val === null || val.id === null) {
            rels[field] = null;
          } else {
            rels[field] = {
              data: this.parseRelationship(relType, val),
            };
          }
        }
      }
    }

    let field;
    for (field in config.fields) {
      if (config.fields[field].readOnly) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete attrs[field];
      } else if (attrs[field] !== undefined && config.fields[field].serialize) {
        attrs[field] = config.fields[field].serialize(attrs[field], attrs);
      }
    }

    data.attributes = attrs;

    if (Object.entries(rels).length) {
      data.relationships = rels;
    }

    return data;
  };

  parseRelationship = (
    type: string,
    attrs: Record<any, any>
  ): { id: string | null; type: string } => {
    const res = this.parseResource(type, attrs);
    return { type: res?.type ?? '', id: res?.id || null };
  };

  deserialize = (res: {
    data?: Record<any, any>;
    included?: Record<any, any>;
    error?: Record<any, any>;
    status?: number;
    errors?: Array<Record<any, any>>;
  }): null | Record<string, any> => {
    if (!res) {
      return null;
    }

    if (res.error) {
      if (isObject(res.error)) {
        return res;
      }
      return {
        error: {
          status: String(res.status || 400),
          title: res.error,
          message: res.error,
        },
      };
    }

    if (res.errors) {
      const error = res.errors.find((e) => e.status !== '422');
      return error ? { error } : { errors: res.errors };
    }

    if (!res.data) {
      return res;
    }

    let { data, included, ...rest } = res;

    if (!Array.isArray(data)) {
      data = [data];
    }

    if (included) {
      data = data.concat(included);
    }

    const fields: Record<string, any> = {};

    Object.keys(this.schema).forEach((ref) => {
      fields[ref] = this.schema[ref].fields;
    });

    data = data.map((rec: Record<string, any>) => {
      const attrs = {
        id: rec.id,
        ...rec.attributes,
      };

      if (fields[rec.type]) {
        let ref;

        let field;
        for (field in fields[rec.type]) {
          ref = fields[rec.type][field];

          if (ref.type) {
            attrs[field] = coerceValue(attrs[field], ref.type);
          }

          if (typeof ref.resolve === 'function') {
            attrs[field] = ref.resolve(attrs[field], attrs, rec);
          }
        }
      }

      return {
        ...rec,
        attributes: attrs,
      };
    });

    data.forEach((rec: Record<string, any>) => {
      if (!rec.relationships) {
        return;
      }

      Object.keys(rec.relationships).forEach((key) => {
        const rel = rec.relationships[key].data;

        if (!rel) return;

        if (Array.isArray(rel)) {
          rec.attributes[key] = rel
            .map((r) => data.find((d: Record<string, any>) => d.type === r.type && d.id === r.id))
            .filter(Boolean)
            .map((r) => r.attributes);
        } else {
          const child = data.find(
            (r: Record<string, any>) => r.type === rel.type && r.id === rel.id
          );
          rec.attributes[key] = child ? child.attributes : null;
        }
      });
    });

    if (Array.isArray(res.data)) {
      data = data.reduce(
        (acc: any[], rec: Record<string, any>) =>
          res.data?.find((r: Record<string, any>) => r.id === rec.id && r.type === rec.type)
            ? acc.concat(rec.attributes)
            : acc,
        []
      );
    } else {
      data = data.find((r: Record<string, any>) => r.id === res.data?.id).attributes;
    }

    return { data, ...rest };
  };
}
